import { h, type FunctionalComponent } from '@stencil/core';
import { DashboardProducts } from '../../dashboard-products/dashboard-products';
import type { Strategies } from '../../../../../../strategies/Strategies';
import type { ResultRenderer } from '../../../../../../../utils/Renderer';
import { DashboardPrivateTile } from '../../dashboard-private/dashboard-private-tile';
import { DashboardDocumentsTile } from '../../dashbord-documents/dashboard-documents-tile';
import {DashboardCompanyTile} from '../../dashboard-company-tile/dashboard-company-tile';
import { MySeDashboardWidgetsMiddle } from '../../dashboard-widgets/myse-dashboard-widgets-middle';
import type { SearchProduct } from '../../../../../../../api/SearchApi';
import { DashboardItemHeader } from '../../dashboard-item-header/dashboard-item-header';
import type {IEolDiscontinuedDetails} from '../../../types/subcomponents-types.type';
import type {SectionsData, WCEventData} from '../../../../../../../api/model/response/documents/SearchResult';
import {useLimitedWidgetDocs} from './helpers';
import {MyseDashboardDesignFirm} from './myse-dashboard-design-firm';
import type { IWidgetRequestData } from '../../../../../../widgets/offsites-widget/offsites-widget-v4/types/offsites-widget-v4.types';
import { SupportChatWidget } from '../../dashboard-widgets/support-chat-widget';

export interface IDashboardDesktopView {
  reflectedTabs: Object,
  searcherNames: Record<string, string>,//eslint-disable-line @typescript-eslint/no-explicit-any
  strategies: Strategies,
  resultRenderer: ResultRenderer,
  resultClickAnalytics: CallableFunction,
  labelProvider: CallableFunction,
  getTabCount: CallableFunction,
  sectionsData?: SectionsData
  eolDiscontinuedResults?: IEolDiscontinuedDetails;
  country: string,
  language: string,
  tabName: string,
  rtl: boolean,
  isOffsitesVisible: boolean,
  faqData,
  queryProp: string,
  query: string,
  relatedProductName?: string,
  priceBanner: boolean,
  productCatalogBanner: boolean,
  brand: string,
  isSubstitutionTool?: boolean;
  isSupport?: boolean;
  isAdditionalConditionsWidgetActive?: boolean;
  offsiteBannersManagerRequestData?: IWidgetRequestData;
  conversationalSearchProductsEndpoint?: string;
  conversationalSearchBrandUrl?: string;
  isConversationalSearchActive?: boolean;
  apigeeAuth?: string;
  apigeeTokenUrl?: string;
  wcEventData?: WCEventData;
  isUnifiedCheckout?: boolean;
  conversationalSearchMedaliaFormId?: string;
}

export const MySeDashboardDesktopView: FunctionalComponent<
  IDashboardDesktopView
> = (props) => {
    const {
      reflectedTabs,
      searcherNames,
      strategies,
      resultRenderer,
      resultClickAnalytics,
      labelProvider,
      country,
      language,
      tabName,
      rtl,
      queryProp,
      getTabCount,
      priceBanner,
      productCatalogBanner,
      brand,
      sectionsData,
      faqData,
      isOffsitesVisible,
      relatedProductName,
      isSubstitutionTool,
      eolDiscontinuedResults,
      isSupport,
      isAdditionalConditionsWidgetActive,
      offsiteBannersManagerRequestData,
      wcEventData,
      isUnifiedCheckout,
      conversationalSearchProductsEndpoint,
      conversationalSearchBrandUrl,
      isConversationalSearchActive,
      apigeeAuth,
      apigeeTokenUrl,
      conversationalSearchMedaliaFormId
    } = props;
    const dashboardState = {
      tab: searcherNames.all,
      ajaxIsLoading: reflectedTabs?.[searcherNames.all]?.ajaxIsLoading,
      hasError: reflectedTabs?.[searcherNames.all]?.hasError,
      count: reflectedTabs?.[searcherNames.all]?.count,
    };

    const renderError = !reflectedTabs?.[searcherNames.all]?.ajaxIsLoading && reflectedTabs?.[searcherNames.all]?.hasError;
    const isDesignFirm = strategies?.strategyData?.cardType?.params?.designFirmWidgets;

    const wrapperCssClass = sectionsData.products?.length && reflectedTabs && !reflectedTabs?.[searcherNames.all]?.ajaxIsLoading ? 'dashboard__products' : '';


    if (isDesignFirm) {
      return [ !!renderError && (
        <uiaas-search-server-error
          query={queryProp}
          labelProvider={(e, f) => labelProvider(e, f)}
          reflectedState={dashboardState}
        />
      ),
      <div class="dashboard-view">
        <section class={wrapperCssClass}>
          <MyseDashboardDesignFirm {...props} />
        </section></div>
      ];
    }


    const priceBannerLabel = labelProvider(
      'redirection.banner.widget.paTool',
      'Please refer to our <b>Price and Availability tool</b> ' +
      'for quick, precise insights into product pricing and availability.',
    );
    const priceBannerComponent = (
      <uiaas-search-widget-cta-banner
        slot="before-results"
        url="/myschneider/priceAndAvailability"
        fluid={true}
        label={priceBannerLabel}
        heading={labelProvider('redirection.banner.widget.paHeading', 'Discover product pricing and availability!')}
        cta={labelProvider('redirection.banner.widget.cta', 'Explore')}
        icon="box2"
      />
    );
    const isDisplayPriceBannerComponent = !productCatalogBanner && priceBanner && !isSupport;
    const productCatalogComponent = <uiaas-search-widget-product-catalog labelProvider={(e, f) => props.labelProvider(e, f)}/>;

    const additionalConditionsWidgetComponent = document.querySelector('uiaas-search-widget-additional-conditions-banner',);
    const shadowRoot = additionalConditionsWidgetComponent?.shadowRoot;
    const isExistAdditionalConditionsWidgetForAllTab = shadowRoot?.querySelector('.additional-conditions-banner') ? true : false;

    const [documentsData, softwareData] = useLimitedWidgetDocs(['documents', 'software'], sectionsData);
    const [ordersData, quotesData] = useLimitedWidgetDocs(['orders', 'quotes'], sectionsData);
    const [trainingsData, topicsData] = useLimitedWidgetDocs(['trainings', 'topics'], sectionsData);
    const [companyData, servicesData] = useLimitedWidgetDocs(['company', 'services'], sectionsData);
    const [supportData, solutionsData] = useLimitedWidgetDocs(['support', 'solutions'], sectionsData);
    const [communityData, videoData] = useLimitedWidgetDocs(['support/community', 'support/video'], sectionsData);


    const conversationalSearch = () => {
        // Not using uiaas-section-loader to ensure instant fetching API data - requirement
        return isConversationalSearchActive ? (
          <uiaas-search-widget-conversational-search-banner-mediator
            country={country}
            language={language}
            conversationalSearchProductsEndpoint={conversationalSearchProductsEndpoint}
            labelProvider={(e, f) => labelProvider(e, f)}
            apigeeAuth={apigeeAuth}
            apigeeTokenUrl={apigeeTokenUrl}
            query={queryProp}
            conversationalSearchBrandUrl={conversationalSearchBrandUrl}
            brand={brand}
            conversationalSearchMedaliaFormId={conversationalSearchMedaliaFormId}
          />
        ) : '';
    };


    return [
      renderError && (
        <uiaas-search-server-error
          query={queryProp}
          labelProvider={(e, f) => labelProvider(e, f)}
          reflectedState={dashboardState}
        />
      ),
      <div class="dashboard-view">
        {productCatalogBanner && (priceBanner || !priceBanner) && !isSupport ? productCatalogComponent : ''}
        {conversationalSearch()}
        {isDisplayPriceBannerComponent && !isExistAdditionalConditionsWidgetForAllTab ? priceBannerComponent : ''}
        {isSupport && <SupportChatWidget term={queryProp}/>}
        {isSupport ? <div
          class={{'support-faq-widget dashboard--full-width': faqData.length > 0}}>
          <uiaas-search-widget-faq
            country={country}
            language={language}
            rtl={rtl}
            maxItems={3}
            tab-name={tabName}
            appName='support'
            termAttribute={queryProp}
            tabName={searcherNames.all}
            tabCount={getTabCount(searcherNames.faq)}
            targetTab={searcherNames.faq}
            dashboardSearcherData={faqData}
            isDashboardView={true}
            labelProvider={(e, f) => labelProvider(e, f)}
          >
            <div slot="dashboard-section-header">
              <DashboardItemHeader
                itemCount={faqData.length}
                labelProvider={(e, f) => labelProvider(e, f)}
                tabCount={getTabCount(searcherNames.faq)}
                targetTab={searcherNames.faq}
                title=""
                term={props.queryProp}
                subTitle={true}
              />
            </div>
          </uiaas-search-widget-faq>
        </div> : ''}
        <section class={wrapperCssClass}>
          <div class="dashboard__tiles">
            <DashboardPrivateTile
              doc={ordersData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.myschneider/orders', 'Orders')}
              targetTab={searcherNames.orders}
              tabCount={getTabCount(searcherNames.orders)}
              strategies={strategies}
              section="orders"
              privateCard={true}
              term={queryProp}
              subTitle={true}
              limitResults={quotesData?.length > 0}
              rtl={rtl}
            ></DashboardPrivateTile>
            <DashboardPrivateTile
              doc={quotesData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.myschneider/quotes', 'Quotes')}
              targetTab={searcherNames.quotes}
              tabCount={getTabCount(searcherNames.quotes)}
              strategies={strategies}
              section="quotes"
              privateCard={true}
              term={queryProp}
              subTitle={true}
              limitResults={ordersData?.length > 0}
              rtl={rtl}
            ></DashboardPrivateTile>
          </div>
          <DashboardProducts
            tabName={reflectedTabs?.[searcherNames?.products]?.tab}
            strategies={strategies}
            resultRenderer={resultRenderer}
            resultClickAnalytics={resultClickAnalytics}
            products={sectionsData.products as SearchProduct[]}
            tabCount={reflectedTabs?.[searcherNames?.products]?.count}
            labelProvider={(e, f) => labelProvider(e, f)}
            country={country}
            language={language}
            isEcommerceMode={false}
            eShop={false}
            rtl={rtl}
            targetTab={searcherNames.products}
            brand={brand}
            subTitle={true}
            query={queryProp}
            isSubstitutionTool={isSubstitutionTool}
            eolDiscontinuedResults={eolDiscontinuedResults}
            isAdditionalConditionsWidgetActive={isAdditionalConditionsWidgetActive}
            isMySeVersion={true}
            isProductData={sectionsData?.products?.length > 0}
            wcEventData={wcEventData}
            isUnifiedCheckout={isUnifiedCheckout}
          />
          {isDisplayPriceBannerComponent && isExistAdditionalConditionsWidgetForAllTab ? priceBannerComponent : ''}

          {!isSupport &&
          <div
            class={{
              dashboard__tiles: isOffsitesVisible,
              'widget-offset-bottom': !isOffsitesVisible,
            }}
          >

            <MySeDashboardWidgetsMiddle
              labelProvider={labelProvider}
              hasCtaProductSelector={!!sectionsData?.products?.length}
              country={country}
              language={language}
              term={queryProp}
              analyticsTabName={'all'}
              brand={brand}
              tabName={tabName}
              isOffsitesWidget={isOffsitesVisible}
              offsiteBannersManagerRequestData={offsiteBannersManagerRequestData}
              relatedProductName={relatedProductName}
              strategies={strategies}
            />

          </div>
          }
          <div class="dashboard__tiles">
            <DashboardPrivateTile
              doc={documentsData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.name.documents', 'Documents')}
              targetTab={searcherNames.documents}
              tabCount={getTabCount(searcherNames.documents)}
              strategies={strategies}
              section="documents"
              privateCard={false}
              limitResults={documentsData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
            <DashboardPrivateTile
              doc={softwareData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.name.software', 'Software')}
              targetTab={searcherNames.software}
              tabCount={getTabCount(searcherNames.software)}
              strategies={strategies}
              section="software"
              privateCard={false}
              limitResults={softwareData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
          </div>
          <div class="dashboard__tiles">
            <DashboardDocumentsTile
              doc={communityData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.name.community', 'Community')}
              targetTab={searcherNames['support/community']}
              tabCount={getTabCount(searcherNames['support/community'])}
              strategies={strategies}
              section="support/community"
              limitResults={communityData?.length > 0}
              query={queryProp}
              rtl={rtl}
              country={country}
              language={language}
            ></DashboardDocumentsTile>
            <DashboardCompanyTile
              doc={videoData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.name.video', 'Video')}
              targetTab={searcherNames['support/video']}
              tabCount={getTabCount(searcherNames['support/video'])}
              strategies={strategies}
              section="support/video"
              limitResults={videoData?.length > 0}
              query={queryProp}
              rtl={rtl}
              country={country}
              language={language}>
            </DashboardCompanyTile>
          </div>
          <div class="dashboard__tiles">
            <DashboardPrivateTile
              doc={trainingsData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.name.trainings', 'Trainings')}
              targetTab={searcherNames.trainings}
              tabCount={getTabCount(searcherNames.trainings)}
              strategies={strategies}
              section="trainings"
              privateCard={true}
              limitResults={trainingsData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
            <DashboardPrivateTile
              doc={topicsData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.name.topics', 'Topics for you')}
              targetTab={searcherNames.topics}
              tabCount={getTabCount(searcherNames.topics)}
              strategies={strategies}
              section="topics"
              privateCard={true}
              limitResults={topicsData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
          </div>
          <div class="dashboard__tiles">
            <DashboardPrivateTile
              doc={companyData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider(
                'tab.name.company',
                'Company'
              )}
              targetTab={searcherNames.company}
              tabCount={getTabCount(searcherNames.company)}
              strategies={strategies}
              section='company'
              limitResults={companyData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
            <DashboardPrivateTile
              doc={servicesData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider(
                'tab.name.services',
                'Topics for you'
              )}
              targetTab={searcherNames.services}
              tabCount={getTabCount(searcherNames.services)}
              strategies={strategies}
              section='services'
              limitResults={servicesData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
          </div>
          <div class="dashboard__tiles">
            <DashboardPrivateTile
              doc={solutionsData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider(
                'tab.name.solutions',
                'Solutions'
              )}
              targetTab={searcherNames.solutions}
              tabCount={getTabCount(searcherNames.solutions)}
              strategies={strategies}
              section='solutions'
              limitResults={ supportData?.length > 0 }
              term={queryProp}
              rtl={rtl}
              customValueClass={solutionsData?.length == 1 ? 'tail__container-limit-height' : ''}
            ></DashboardPrivateTile>

            <DashboardPrivateTile
              doc={supportData}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider(
                'tab.name.support',
                'Support'
              )}
              targetTab={searcherNames.support}
              tabCount={getTabCount(searcherNames.support)}
              strategies={strategies}
              section='support'
              limitResults={supportData.length < 3}
              term={queryProp}
              rtl={rtl}
            ></DashboardPrivateTile>
          </div>
          {!isSupport ? <div class={{'dashboard--full-width': faqData.length > 0}}>
            <uiaas-search-widget-faq
              country={country}
              language={language}
              rtl={rtl}
              maxItems={3}
              tab-name={tabName}
              termAttribute={queryProp}
              tabName={searcherNames.all}
              tabCount={getTabCount(searcherNames.faq)}
              targetTab={searcherNames.faq}
              dashboardSearcherData={faqData}
              isDashboardView={true}
              labelProvider={(e, f) => labelProvider(e, f)}
            >
              <div slot="dashboard-section-header">
                <DashboardItemHeader
                  itemCount={faqData.length}
                  labelProvider={(e, f) => labelProvider(e, f)}
                  tabCount={getTabCount(searcherNames.faq)}
                  targetTab={searcherNames.faq}
                  title=""
                  term={props.queryProp}
                  subTitle={true}
                />
              </div>
            </uiaas-search-widget-faq>
          </div> : ''}
        </section>
      </div>,
    ];
};
